import { useState, useCallback } from 'react';
import { useWardrobe } from '../../context/WardrobeContext';
import { Capsule } from '../../types';
import { CapsuleFormData } from '../../components/features/wardrobe/forms/CapsuleForm';

/**
 * Hook for managing capsule operations (add, update, delete)
 * PERFORMANCE OPTIMIZATION: Uses WardrobeContext with lazy loading
 * to prevent blocking main thread on initial page load.
 */
export const useCapsuleManagement = (modalState?: ReturnType<typeof import('./useModalState').useModalState>) => {
  // Use wardrobe context with lazy loading instead of direct useCapsules hook
  const {
    addCapsule,
    updateCapsule,
    deleteCapsule,
    loadCapsules
  } = useWardrobe();
  
  // Local state for capsule management
  const [selectedCapsule, setSelectedCapsule] = useState<Capsule | undefined>(undefined);
  
  /**
   * View a capsule by setting it as the selected capsule
   */
  const handleViewCapsule = useCallback((capsule: Capsule) => {
    console.log('[useCapsuleManagement] handleViewCapsule called:', capsule.id, capsule.name);
    setSelectedCapsule(capsule);
    
    // Open the modal directly if modalState is provided
    if (modalState?.setIsViewCapsuleModalOpen) {
      console.log('[useCapsuleManagement] Opening view modal via modalState');
      modalState.setIsViewCapsuleModalOpen(true);
    }
  }, [modalState]);
  
  /**
   * Edit a capsule
   */
  const handleEditCapsule = useCallback((capsule: Capsule) => {
    setSelectedCapsule(capsule);
  }, []);
  
  /**
   * Delete a capsule by ID
   */
  const handleDeleteCapsule = useCallback(async (id: string) => {
    await loadCapsules(); // Ensure capsules are loaded
    await deleteCapsule(id);
    setSelectedCapsule(undefined);
  }, [deleteCapsule, loadCapsules]);
  
  /**
   * Handle edit capsule submit
   */
  const handleEditCapsuleSubmit = useCallback(async (id: string, data: CapsuleFormData) => {
    // Log the incoming form data
    console.log('[useCapsuleManagement] Edit capsule form data:', data);
    
    const capsuleData = {
      name: data.name,
      scenario: data.scenario,
      // Include the full scenarios array to ensure it's properly saved
      scenarios: data.scenarios || [],
      seasons: data.seasons,
      selectedItems: data.selectedItems,
      mainItemId: data.mainItemId || ''
    };
    
    console.log('[useCapsuleManagement] Updating capsule with data:', capsuleData);
    
    await loadCapsules(); // Ensure capsules are loaded
    await updateCapsule(id, capsuleData);
  }, [updateCapsule, loadCapsules]);
  
  /**
   * Add a new capsule
   */
  const handleAddCapsule = useCallback(async (id: string, data: CapsuleFormData) => {
    try {
      // For new capsules, we ignore the id parameter (it will be 'new')
      const capsuleData = {
        name: data.name,
        style: '', // Default empty style since it's required by the Capsule type
        scenario: data.scenarios?.[0] || '', // Use first scenario or empty string
        scenarios: data.scenarios || [],
        seasons: data.seasons,
        selectedItems: data.selectedItems || [],
        mainItemId: data.mainItemId || ''
      };
      
      // Ensure capsules are loaded and add the capsule
      await loadCapsules(); // Ensure capsules are loaded  
      await addCapsule(capsuleData);
    } catch (error) {
      console.error('Error adding capsule:', error);
      throw error; // Re-throw to handle in the component
    }
  }, [addCapsule, loadCapsules]);
  
  return {
    // State
    selectedCapsule,
    setSelectedCapsule,
    
    // Handlers
    handleViewCapsule,
    handleEditCapsule,
    handleDeleteCapsule,
    handleEditCapsuleSubmit,
    handleAddCapsule,
  };
};
